import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';

const TABS = [
  { id: 'customers', label: 'Customers' },
  { id: 'invoices', label: 'Invoices' },
  { id: 'accounts', label: 'Accounts' },
  { id: 'items', label: 'Items' },
  { id: 'vendors', label: 'Vendors' },
  { id: 'bills', label: 'Bills' },
  { id: 'payments', label: 'Payments' },
  { id: 'estimates', label: 'Estimates' },
  { id: 'purchaseorders', label: 'Purchase Orders' },
  { id: 'employees', label: 'Employees' },
];

const formatCurrency = (val) => {
  if (val == null) return '—';
  return '$' + Number(val).toLocaleString('en-US', { minimumFractionDigits: 2 });
};

const formatDate = (val) => {
  if (!val) return '—';
  return new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const COLUMNS = {
  customers: [
    { key: 'DisplayName', label: 'Name' },
    { key: 'PrimaryEmailAddr', label: 'Email', render: (v) => v?.Address || '—' },
    { key: 'PrimaryPhone', label: 'Phone', render: (v) => v?.FreeFormNumber || '—' },
    { key: 'Balance', label: 'Balance', render: formatCurrency, align: 'right' },
  ],
  invoices: [
    { key: 'DocNumber', label: 'Invoice #' },
    { key: 'CustomerRef', label: 'Customer', render: (v) => v?.name || '—' },
    { key: 'TxnDate', label: 'Date', render: formatDate },
    { key: 'TotalAmt', label: 'Total', render: formatCurrency, align: 'right' },
    { key: 'Balance', label: 'Balance', render: formatCurrency, align: 'right' },
  ],
  accounts: [
    { key: 'Name', label: 'Name' },
    { key: 'AccountType', label: 'Type' },
    { key: 'AccountSubType', label: 'Sub-type' },
    { key: 'CurrentBalance', label: 'Balance', render: formatCurrency, align: 'right' },
  ],
  items: [
    { key: 'Name', label: 'Name' },
    { key: 'Type', label: 'Type' },
    { key: 'UnitPrice', label: 'Price', render: formatCurrency, align: 'right' },
    { key: 'QtyOnHand', label: 'Qty on Hand', render: (v) => v != null ? v : '—', align: 'right' },
  ],
  vendors: [
    { key: 'display_name', label: 'Name' },
    { key: 'email', label: 'Email' },
    { key: 'phone', label: 'Phone' },
    { key: 'company_name', label: 'Company' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'is_1099', label: '1099', render: (v) => v ? 'Yes' : '—' },
  ],
  bills: [
    { key: 'DocNumber', label: 'Bill #' },
    { key: 'VendorRef', label: 'Vendor', render: (v) => v?.name || '—' },
    { key: 'TxnDate', label: 'Date', render: formatDate },
    { key: 'DueDate', label: 'Due Date', render: formatDate },
    { key: 'TotalAmt', label: 'Total', render: formatCurrency, align: 'right' },
    { key: 'Balance', label: 'Balance', render: formatCurrency, align: 'right' },
  ],
  payments: [
    { key: 'CustomerRef', label: 'Customer', render: (v) => v?.name || '—' },
    { key: 'TxnDate', label: 'Date', render: formatDate },
    { key: 'TotalAmt', label: 'Amount', render: formatCurrency, align: 'right' },
    { key: 'PaymentMethodRef', label: 'Method', render: (v) => v?.name || '—' },
    { key: 'DepositToAccountRef', label: 'Deposit To', render: (v) => v?.name || '—' },
  ],
  estimates: [
    { key: 'DocNumber', label: 'Estimate #' },
    { key: 'CustomerRef', label: 'Customer', render: (v) => v?.name || '—' },
    { key: 'TxnDate', label: 'Date', render: formatDate },
    { key: 'ExpirationDate', label: 'Expires', render: formatDate },
    { key: 'TotalAmt', label: 'Total', render: formatCurrency, align: 'right' },
    { key: 'TxnStatus', label: 'Status' },
  ],
  purchaseorders: [
    { key: 'DocNumber', label: 'PO #' },
    { key: 'VendorRef', label: 'Vendor', render: (v) => v?.name || '—' },
    { key: 'TxnDate', label: 'Date', render: formatDate },
    { key: 'TotalAmt', label: 'Total', render: formatCurrency, align: 'right' },
    { key: 'POStatus', label: 'Status' },
  ],
  employees: [
    { key: 'DisplayName', label: 'Name' },
    { key: 'PrimaryEmailAddr', label: 'Email', render: (v) => v?.Address || '—' },
    { key: 'PrimaryPhone', label: 'Phone', render: (v) => v?.FreeFormNumber || '—' },
    { key: 'Active', label: 'Active', render: (v) => v ? 'Yes' : 'No' },
  ],
};

const ENTITY_KEYS = {
  customers: 'Customer',
  invoices: 'Invoice',
  accounts: 'Account',
  items: 'Item',
  vendors: 'Vendor',
  bills: 'Bill',
  payments: 'Payment',
  estimates: 'Estimate',
  purchaseorders: 'PurchaseOrder',
  employees: 'Employee',
};

const DataView = ({ loaded, onNavigateToIntegrations }) => {
  const [activeTab, setActiveTab] = useState('customers');
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notConnected, setNotConnected] = useState(false);
  const fetchedRef = useRef({});

  useEffect(() => {
    if (notConnected) return;
    if (fetchedRef.current[activeTab]) return;
    fetchData(activeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, notConnected]);

  const fetchData = async (entity) => {
    if (notConnected) return;
    setLoading(true);
    setError(null);
    try {
      // Vendors come from Arbcor's own table; everything else from QB passthrough
      const isVendors = entity === 'vendors';
      const url = isVendors ? '/api/vendors' : `/api/quickbooks/data/${entity}`;
      const response = await apiFetch(url);
      const result = await response.json();
      if (!response.ok) {
        if (result.error === 'QuickBooks not connected' || result.reconnect) {
          setNotConnected(true);
          return;
        }
        throw new Error(result.error || 'Failed to fetch data');
      }
      const rows = isVendors
        ? result.vendors || []
        : result.data?.[ENTITY_KEYS[entity]] || [];
      setData((prev) => ({ ...prev, [entity]: rows }));
      fetchedRef.current[entity] = true;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const columns = COLUMNS[activeTab];
  const rows = data[activeTab] || [];

  if (notConnected) {
    return (
      <div className="max-w-[1000px] mx-auto">
        <div
          className="mb-8 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(12px)',
            transitionDelay: '300ms',
          }}
        >
          <h1 className="text-2xl font-light text-zinc-900 mb-1">Data</h1>
          <p className="text-sm text-zinc-400">View your QuickBooks financial data</p>
        </div>

        <div
          className="bg-white border border-zinc-100 rounded-lg p-12 text-center transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{
            opacity: loaded ? 1 : 0,
            transform: loaded ? 'translateY(0)' : 'translateY(12px)',
            transitionDelay: '400ms',
          }}
        >
          <div className="w-12 h-12 rounded-xl bg-zinc-100 flex items-center justify-center mx-auto mb-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-zinc-400" strokeLinecap="round" strokeLinejoin="round">
              <ellipse cx="12" cy="6" rx="8" ry="3" />
              <path d="M4 6v6c0 1.66 3.58 3 8 3s8-1.34 8-3V6" />
              <path d="M4 12v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" />
            </svg>
          </div>
          <h3 className="text-lg font-light text-zinc-900 mb-2">Connect QuickBooks to view data</h3>
          <p className="text-sm text-zinc-400 mb-6">Link your QuickBooks account to see customers, invoices, accounts, and more.</p>
          <button
            onClick={onNavigateToIntegrations}
            className="px-6 py-2.5 bg-zinc-900 text-white text-[11px] uppercase tracking-[0.15em] hover:bg-zinc-800 rounded-md transition-colors duration-300"
          >
            Go to Integrations
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto">
      {/* Header */}
      <div
        className="mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(12px)',
          transitionDelay: '300ms',
        }}
      >
        <h1 className="text-2xl font-light text-zinc-900 mb-1">Data</h1>
        <p className="text-sm text-zinc-400">View your QuickBooks financial data</p>
      </div>

      {/* Sub-tabs */}
      <div
        className="flex items-center gap-1 mb-6 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(8px)',
          transitionDelay: '400ms',
        }}
      >
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm rounded-md transition-colors duration-300 ${
              tab.id === activeTab
                ? 'bg-zinc-900 text-white'
                : 'text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
          <svg className="w-4 h-4 text-red-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01" />
          </svg>
          <span className="text-sm text-red-600">{error}</span>
          <button onClick={() => { setError(null); fetchData(activeTab); }} className="ml-auto text-xs text-red-500 hover:text-red-700 uppercase tracking-wider">
            Retry
          </button>
        </div>
      )}

      {/* Table */}
      <div
        className="bg-white border border-zinc-100 rounded-lg overflow-hidden transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{
          opacity: loaded ? 1 : 0,
          transform: loaded ? 'translateY(0)' : 'translateY(16px)',
          transitionDelay: '500ms',
        }}
      >
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-6 h-6 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <div className="py-16 text-center">
            <p className="text-sm text-zinc-400">No {activeTab} found</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-zinc-100">
                    {columns.map((col) => (
                      <th
                        key={col.key}
                        className={`px-5 py-3 text-[10px] uppercase tracking-[0.2em] text-zinc-400 font-medium ${
                          col.align === 'right' ? 'text-right' : 'text-left'
                        }`}
                      >
                        {col.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr
                      key={row.Id || row.id || i}
                      className="border-b border-zinc-50 last:border-0 hover:bg-zinc-50/50 transition-colors"
                      style={{
                        opacity: loaded ? 1 : 0,
                        transition: `opacity 0.5s cubic-bezier(0.16,1,0.3,1) ${550 + i * 30}ms`,
                      }}
                    >
                      {columns.map((col) => {
                        const raw = row[col.key];
                        const display = col.render ? col.render(raw) : (raw ?? '—');
                        return (
                          <td
                            key={col.key}
                            className={`px-5 py-3.5 text-sm ${
                              col.align === 'right' ? 'text-right tabular-nums' : ''
                            } ${col.key === columns[0].key ? 'font-medium text-zinc-900' : 'text-zinc-500'}`}
                          >
                            {display}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="px-5 py-3 border-t border-zinc-100">
              <span className="text-[10px] text-zinc-400">
                {rows.length} {activeTab}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DataView;
